import { useQueryClient } from "@tanstack/react-query";
import { LogOut, Menu, Settings2, UserCircle2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";

import { signOut } from "@/lib/auth";
import { useFavicon } from "@/lib/use-favicon";
import { useRoleProfile } from "@/lib/use-role-profile";
import { cn } from "@/lib/utils";

import { Breadcrumbs } from "./Breadcrumbs";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { PreferencesPopover } from "./PreferencesPopover";
import { Sidebar } from "./Sidebar";
import { PartnerSwitcher } from "./PartnerSwitcher";

/**
 * Main layout for every authenticated route.
 *
 * Grid at md+: [sidebar | content]; below md the sidebar slides in as
 * a drawer over a dim backdrop. Topbar holds breadcrumbs, partner
 * switcher, preferences, and sign-out. Routed pages render into
 * <Outlet/> under the topbar.
 */
export function AppShell(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const profile = useRoleProfile();
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const callerName =
    [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim() ||
    profile?.email ||
    "";
  // Sync browser tab icon + title to the active partner's brand.
  // Lives here because AppShell is the lowest authenticated ancestor
  // that's rendered exactly once across every routed page.
  useFavicon();

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === ",") {
        e.preventDefault();
        setPrefsOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function handleSignOut(): void {
    signOut();
    queryClient.clear();
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* WCAG 4.1.3: live region for dynamic status announcements. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        id="admin-live-region"
      />

      {/* Skip-to-main link - visible only on keyboard focus, lets
          screen-reader and keyboard-only users jump past topbar +
          sidebar straight into page content (WCAG 2.4.1 Bypass
          Blocks). */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:text-primary-foreground focus:shadow-md"
      >
        Skip to main content
      </a>

      {/* Desktop sidebar */}
      <div className="fixed inset-y-0 left-0 hidden md:flex">
        <Sidebar />
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 z-30 bg-black/40 md:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="fixed inset-y-0 left-0 z-40 md:hidden">
            <Sidebar onNavigate={() => setDrawerOpen(false)} />
          </div>
        </>
      )}

      <div className="md:pl-60">
        {/* Topbar wraps on extreme narrow; flex-wrap + gap-y keeps layout
            from bursting out horizontally on sub-360px devices. Horizontal
            padding tracks the <main> below it (px-4 / md:px-8) so the
            breadcrumb starts at the same gutter as the page content
            underneath - prior px-3 made the breadcrumb look like it was
            touching the viewport edge. */}
        <header className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-x-2 gap-y-2 border-b bg-background/90 px-6 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/75 md:px-10">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <button
              type="button"
              aria-label={drawerOpen ? "Close menu" : "Open menu"}
              onClick={() => setDrawerOpen((v) => !v)}
              className="flex-shrink-0 rounded-md border bg-card p-2 text-muted-foreground hover:bg-muted hover:text-foreground md:hidden"
            >
              {drawerOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
            {/* Hide breadcrumbs below sm - they'd wrap or push the switcher
                off-screen on 320-375px phones. The sidebar nav covers the
                same ground visually on mobile. */}
            <div className="hidden min-w-0 truncate sm:block">
              <Breadcrumbs />
            </div>
          </div>

          <div className="relative flex min-w-0 flex-shrink items-center gap-1.5 sm:gap-2">
            <PartnerSwitcher />
            {callerName && (
              <div
                className="hidden min-w-0 items-center gap-1.5 rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground sm:flex"
                title={profile?.email ?? callerName}
              >
                <UserCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate font-medium text-foreground">{callerName}</span>
              </div>
            )}
            <button
              type="button"
              onClick={() => setPrefsOpen((v) => !v)}
              aria-label="Open preferences"
              title="Preferences (⌘,)"
              className={cn(
                "flex-shrink-0 rounded-md border bg-card p-2 text-muted-foreground",
                "hover:bg-muted hover:text-foreground",
              )}
            >
              <Settings2 className="h-4 w-4" />
            </button>
            {/* Icon-only on mobile; text label kicks in at sm+. */}
            <button
              type="button"
              onClick={handleSignOut}
              aria-label="Sign out"
              title="Sign out"
              className="flex flex-shrink-0 items-center gap-2 rounded-md border bg-card p-2 text-muted-foreground hover:bg-muted hover:text-foreground sm:px-3 sm:py-1.5 sm:text-sm sm:text-foreground"
            >
              <LogOut className="h-4 w-4 sm:hidden" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
            <PreferencesPopover open={prefsOpen} onClose={() => setPrefsOpen(false)} />
          </div>
        </header>

        <ImpersonationBanner />

        <main id="main" className="px-6 py-6 md:px-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
